import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import type { TodoItem } from '@/lib/types';
import type { NostrEvent } from '@nostrify/nostrify';

const TODO_TAG = 'cryptodo';
const TODO_KIND = 30078;

/** Generate a d-tag value for a todo. */
function todoDTag(id: string): string {
  return `${TODO_TAG}:${id}`;
}

/** Generate a simple UUID-like ID. */
function generateId(): string {
  return crypto.randomUUID();
}

/** Decrypt a single todo event into a TodoItem (or null if decryption fails). */
async function decryptTodoEvent(
  event: NostrEvent,
  decryptFn: (pubkey: string, ciphertext: string) => Promise<string>,
  userPubkey: string,
): Promise<TodoItem | null> {
  try {
    const plaintext = await decryptFn(userPubkey, event.content);
    const data = JSON.parse(plaintext) as TodoItem;
    // Extract id from d-tag
    const dTag = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
    const id = dTag.startsWith(`${TODO_TAG}:`) ? dTag.slice(TODO_TAG.length + 1) : dTag;
    return { ...data, id };
  } catch {
    console.warn('Failed to decrypt todo event', event.id);
    return null;
  }
}

/** Hook to fetch and decrypt all of the current user's todos. */
export function useTodos() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['todos', user?.pubkey],
    queryFn: async (c) => {
      if (!user?.signer.nip44) throw new Error('NIP-44 not supported');

      const events = await nostr.query(
        [{ kinds: [TODO_KIND], authors: [user.pubkey], '#t': [TODO_TAG] }],
        { signal: c.signal },
      );

      const todos: TodoItem[] = [];
      for (const event of events) {
        const todo = await decryptTodoEvent(event, user.signer.nip44.decrypt.bind(user.signer.nip44), user.pubkey);
        if (todo) todos.push(todo);
      }

      return todos.sort((a, b) => a.createdAt - b.createdAt);
    },
    enabled: !!user?.signer.nip44,
  });
}

/** Hook to create or update an encrypted todo event. */
export function usePublishTodo() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (todo: Omit<TodoItem, 'id' | 'createdAt'> & { id?: string; createdAt?: number }) => {
      if (!user?.signer.nip44) throw new Error('NIP-44 not supported');

      const id = todo.id ?? generateId();
      const createdAt = todo.createdAt ?? Math.floor(Date.now() / 1000);

      const payload: TodoItem = {
        id,
        title: todo.title,
        date: todo.date,
        completed: todo.completed,
        description: todo.description,
        priority: todo.priority,
        createdAt,
      };

      const ciphertext = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(payload));

      const tags: string[][] = [
        ['d', todoDTag(id)],
        ['t', TODO_TAG],
        ['alt', 'Encrypted to-do item (CrypToDo)'],
      ];

      // Add client tag
      if (location.protocol === 'https:') {
        tags.push(['client', location.hostname]);
      }

      const event = await user.signer.signEvent({
        kind: TODO_KIND,
        content: ciphertext,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      await nostr.event(event, { signal: AbortSignal.timeout(5000) });
      return payload;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', user?.pubkey] });
    },
    onError: (error) => {
      console.error('Failed to publish todo:', error);
    },
  });
}

/** Hook to delete a todo event. */
export function useDeleteTodo() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (todoId: string) => {
      if (!user) throw new Error('Not logged in');

      // Publish a kind 5 delete request targeting the addressable event
      const tags: string[][] = [
        ['a', `${TODO_KIND}:${user.pubkey}:${todoDTag(todoId)}`],
      ];

      if (location.protocol === 'https:') {
        tags.push(['client', location.hostname]);
      }

      const event = await user.signer.signEvent({
        kind: 5,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      await nostr.event(event, { signal: AbortSignal.timeout(5000) });
      return todoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', user?.pubkey] });
    },
    onError: (error) => {
      console.error('Failed to delete todo:', error);
    },
  });
}
